import { Injectable, signal, inject, DestroyRef } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ClientsService } from '../../../../core/services/clients/clients.service';
import { MemberRow } from '../models/member-row';

@Injectable()
export class ConstitutionAdministrationStore {
  private readonly clientsService = inject(ClientsService);
  private readonly fb = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);

  readonly administrationForm: FormGroup = this.fb.nonNullable.group({
    id: this.fb.control<number | null>(null),
    administrationSystem: this.fb.control<string | null>(null)
  });

  readonly memberModalForm: FormGroup = this.fb.nonNullable.group({
    id: this.fb.control<number | null>(null),
    associatedClient: this.fb.control<number | null>(null),
    name: this.fb.nonNullable.control<string>(''),
    documentNumber: this.fb.nonNullable.control<string>(''),
    address: this.fb.nonNullable.control<string>(''),
    representativeId: this.fb.control<number | null>(null),
    representativeName: this.fb.nonNullable.control<string>(''),
    representativeNif: this.fb.nonNullable.control<string>(''),
    cargo: this.fb.nonNullable.control<string>(''),
    cargoStartDate: this.fb.control<Date | null>(null),
    cargoEndDate: this.fb.control<Date | null>(null),
    isCeo: this.fb.nonNullable.control<boolean>(false),
    ceoStartDate: this.fb.control<Date | null>(null),
    ceoEndDate: this.fb.control<Date | null>(null),
    hasRetribution: this.fb.nonNullable.control<boolean>(false),
    retributionKeys: this.fb.nonNullable.control<string[]>([]),
    retributionAmountA: this.fb.control<number | null>(null),
    retributionAmountE: this.fb.control<number | null>(null),
    retributionAmountG: this.fb.control<number | null>(null),
    inStatutes: this.fb.nonNullable.control<boolean>(false),
    retributionStartDate: this.fb.control<Date | null>(null),
    hasContract: this.fb.nonNullable.control<boolean>(false),
    hasMinutes: this.fb.nonNullable.control<boolean>(false),
    socialSecurityRegime: this.fb.nonNullable.control<string>(''),
    classification: this.fb.nonNullable.control<string>(''),
    selfEmployedChargedTo: this.fb.nonNullable.control<string>(''),
    selfEmployedChargedName: this.fb.nonNullable.control<string>(''),
    iban: this.fb.nonNullable.control<string>(''),
    monthlyNetIncome: this.fb.control<number | null>(null),
    mutualAccidents: this.fb.nonNullable.control<string>(''),
    mutualCommonContingencies: this.fb.nonNullable.control<string>(''),
    signsCCAA: this.fb.nonNullable.control<boolean>(false),
    certifiesCCAA: this.fb.nonNullable.control<boolean>(false),
    signsIS: this.fb.nonNullable.control<boolean>(false),
    certifiesIS: this.fb.nonNullable.control<boolean>(false),
    signsLaborContracts: this.fb.nonNullable.control<boolean>(false),
    signsLaborDocuments: this.fb.nonNullable.control<boolean>(false),
    observations: this.fb.nonNullable.control<string>('')
  });

  readonly members = signal<MemberRow[]>([]);
  readonly nextMemberId = signal<number>(1);
  readonly membersTableConfig = signal<any | null>(null);
  readonly isMemberModalActive = signal<boolean>(false);
  readonly isEditingMember = signal<boolean>(false);
  readonly clients = signal<Array<{ id: number; name: string; nif?: string }>>([]);
  readonly bankAccounts = signal<Array<{ id: number; iban: string; endDate?: string | null }>>([]);
  readonly representativeOptions = signal<Array<{ id: number; name: string; nif?: string }>>([]);

  // Logic for representatives similar to Socios
  readonly representatives = signal<Array<{ id: number; name: string; nif?: string }>>([]);

  editingMemberId: number | null = null;

  readonly adminSystemOptions = [
    { label: 'Administrador Unico', value: 'ADMINISTRADOR_UNICO' },
    { label: 'Administradores Solidarios', value: 'ADMINISTRADORES_SOLIDARIOS' },
    { label: 'Administradores Mancomunados', value: 'ADMINISTRADORES_MANCOMUNADOS' },
    { label: 'Consejo de Administracion', value: 'CONSEJO_DE_ADMINISTRACION' },
    { label: 'Patronato', value: 'PATRONATO' },
    { label: 'Asamblea', value: 'ASAMBLEA' },
    { label: 'Otros', value: 'OTROS' }
  ];

  readonly cargoOptions = [
    { label: 'Presidente', value: 'PRESIDENTE' },
    { label: 'Vicepresidente', value: 'VICEPRESIDENTE' },
    { label: 'Secretario Consejero', value: 'SECRETARIO_CONSEJERO' },
    { label: 'Secretario no consejero', value: 'SECRETARIO_NO_CONSEJERO' },
    { label: 'Vocal', value: 'VOCAL' }
  ];

  readonly retributionKeyOptions = [
    { label: 'A (Rendimiento del trabajo por cuenta ajena)', value: 'A' },
    { label: 'E (Retribucion a consejero y administrador)', value: 'E' },
    { label: 'G (Rendimientos por actividades profesionales)', value: 'G' }
  ];

  init(): void {
    this.memberModalForm.get('associatedClient')?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(id => {
        if (!id) {
          this.representativeOptions.set([]);
          this.memberModalForm.patchValue({ representativeId: null, representativeName: '', representativeNif: '' });
          return;
        }
        const selected = this.clients().find(c => c.id === id);
        if (selected) {
          // fill name and document number
          const doc = (selected as any).documentNumber || (selected as any).nif;
          this.memberModalForm.patchValue({
            name: selected.name,
            documentNumber: doc || ''
          });
          void this.autofillAddressForMember(id);
          void this.loadBankAccountsForMember(id);
          void this.loadRepresentativeOptionsForClient(id);
        }
      });

    this.memberModalForm.get('representativeId')?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(repId => {
        const selected = this.representativeOptions().find(r => r.id === repId);
        this.memberModalForm.patchValue({
          representativeName: selected?.name || '',
          representativeNif: selected?.nif || ''
        }, { emitEvent: false });
      });

    this.buildMembersTableConfig();
    void this.loadClients();
  }

  private async loadClients(): Promise<void> {
    try {
      this.clients.set(await this.clientsService.getClientsNames());
    } catch {
      this.clients.set([]);
    }
  }

  private async loadRepresentativeOptionsForClient(clientId: number): Promise<void> {
    try {
      const client = await this.clientsService.getClientById(clientId);
      const personType = client?.personType;

      if (personType === 'JURIDIC_PERSON') {
        const administration = await this.clientsService.getAdministrationByClient(clientId);
        const adminId = administration?.id || administration?.administration?.id;
        if (!adminId) {
          this.representativeOptions.set([]);
          return;
        }
        const members = await this.clientsService.getMembersByAdministration(adminId);
        const options = (members || [])
          .filter((m: any) => m?.associatedClient?.id || m?.associatedClient) // Only allow members linked to a Client
          .map((m: any) => ({
            id: m?.associatedClient?.id ?? m?.associatedClient, // Use Client ID
            name: m?.name || '',
            nif: m?.documentNumber || m?.nif || '',
            associatedClient: m?.associatedClient?.id ?? m?.associatedClient
          }))
          .filter((m: any) => m.name && m.id);
        this.representativeOptions.set(options);
        return;
      }

      if (personType === 'PHYSICAL_PERSON') {
        const relatedPersons = await this.clientsService.getClientRelatedPersons(clientId);
        const options = (relatedPersons || [])
          .filter((p: any) => p?.person && (p.person.id || p.person)) // Only allow related persons linked to a Client
          .map((p: any) => {
            const person = typeof p?.person === 'object' && p?.person !== null ? p.person : p.person;
            return {
              id: person?.id ?? person, // Use Client ID
              name: person?.name || p?.name || '',
              nif: p?.documents?.number || p?.documentNumber || person?.documentNumber || person?.nif || '',
              associatedClient: person?.id ?? person
            };
          })
          .filter((p: any) => p.name && p.id);
        this.representativeOptions.set(options);
        return;
      }

      this.representativeOptions.set([]);
    } catch {
      this.representativeOptions.set([]);
    }
  }

  async loadAdministrationData(clientId: number): Promise<void> {
    try {
      const adminRes = await this.clientsService.getAdministrationByClient(clientId);

      const administratorMembers = adminRes?.id
        ? await this.clientsService.getMembersByAdministration(adminRes.id)
        : [];

      if (administratorMembers && Array.isArray(administratorMembers) && administratorMembers.length > 0) {
        (adminRes as any).members = administratorMembers;
      }

      const administrationData = (adminRes as any)?.administration ?? adminRes;
      const adminMembers = Array.isArray(adminRes)
        ? adminRes
        : (adminRes as any)?.members ?? (adminRes as any)?.administrationMembers ?? [];

      const administrationSystem = administrationData?.administrationSystem || adminMembers[0]?.administrationSystem || null;

      this.administrationForm.patchValue({
        id: administrationData?.id || null,
        administrationSystem: administrationSystem || null
      }, { emitEvent: false });

      if (adminMembers.length > 0) {
        let tempId = this.nextMemberId();

        const mapClientToRepresentative = (r: any) => {
          const id = r?.id ?? (typeof r === 'number' ? r : null);
          if (id == null) return null;
          const first = r?.firstSurname ?? r?.first_surname ?? '';
          const second = r?.secondSurname ?? r?.second_surname ?? '';
          const given = r?.name ?? '';
          const fullName = [first, second, given].filter(Boolean).join(' ').trim() || given || '';
          const nif = r?.documentNumber ?? r?.document_number ?? r?.nif ?? '';
          return { id, name: fullName, nif, associatedClient: id };
        };

        const mappedMembers = adminMembers.map((m: any) => {
          const derivedId = m.id ?? m.memberId ?? tempId++;

          let representatives: Array<{ id: number; name: string; nif?: string; associatedClient?: number }> = [];
          if (Array.isArray(m?.representatives) && m.representatives.length > 0) {
            representatives = m.representatives
              .map((r: any) => mapClientToRepresentative(r))
              .filter((x: any): x is { id: number; name: string; nif?: string; associatedClient?: number } => x != null && (x.name || x.id));
          }

          return {
            id: derivedId,
            associatedClient: m.associatedClient?.id ?? m.associatedClient ?? null,
            name: m.name,
            documentNumber: m.documentNumber,
            address: m.address,
            representativeName: m.representativeName,
            representativeNif: m.representativeNif,
            // Cada miembro conserva su sistema; los anteriores a esta propiedad toman el del órgano.
            administrationSystem: m.administrationSystem ?? administrationSystem ?? null,
            cargo: m.cargo,
            cargoStartDate: m.cargoStartDate ? new Date(m.cargoStartDate) : null,
            cargoEndDate: m.cargoEndDate ? new Date(m.cargoEndDate) : null,
            isCeo: m.isCeo,
            ceoStartDate: m.ceoStartDate ? new Date(m.ceoStartDate) : null,
            ceoEndDate: m.ceoEndDate ? new Date(m.ceoEndDate) : null,
            hasRetribution: m.hasRetribution,
            retributionKeys: m.retributionKeys || [],
            retributionAmountA: m.retributionAmountA,
            retributionAmountE: m.retributionAmountE,
            retributionAmountG: m.retributionAmountG,
            inStatutes: m.inStatutes,
            retributionStartDate: m.retributionStartDate ? new Date(m.retributionStartDate) : null,
            hasContract: m.hasContract,
            hasMinutes: m.hasMinutes,
            socialSecurityRegime: m.socialSecurityRegime,
            classification: m.classification,
            selfEmployedChargedTo: m.selfEmployedChargedTo,
            selfEmployedChargedName: m.selfEmployedChargedName,
            iban: m.iban,
            monthlyNetIncome: m.monthlyNetIncome,
            mutualAccidents: m.mutualAccidents,
            mutualCommonContingencies: m.mutualCommonContingencies,
            signsCCAA: m.signsCCAA,
            certifiesCCAA: m.certifiesCCAA,
            signsIS: m.signsIS,
            certifiesIS: m.certifiesIS,
            signsLaborContracts: m.signsLaborContracts,
            signsLaborDocuments: m.signsLaborDocuments,
            representatives: representatives,
            observations: m.observations ?? ''
          } as MemberRow & { representatives: any[] };
        });

        this.members.set(mappedMembers);
        const maxIdMembers = mappedMembers.reduce((max: number, m: MemberRow) => Math.max(max, m.id || 0), 0);
        this.nextMemberId.set(Math.max(tempId, maxIdMembers + 1));
        this.buildMembersTableConfig();
      }
    } catch (error) {
      console.error('Error loading Administration data:', error);
    }
  }

  onAdminSystemChange(selectedType: string | null, clientId: number | null): Promise<any> {
    this.administrationForm.get('administrationSystem')?.setValue(selectedType);
    if (!clientId) return Promise.resolve(null);
    return this.clientsService.saveClientAdministrators(this.administrationForm.value, clientId)
      .then(administration => {
        this.administrationForm.patchValue({ id: administration?.id });
        return administration;
      });
  }

  buildMembersTableConfig(): void {
    const headers = [
      { name: 'Nombre', key: 'name' },
      { name: 'NIF', key: 'documentNumber' },
      { name: 'Cargo', key: 'cargoLabel' },
      { name: 'Fecha de alta', key: 'cargoStartDateFormatted' },
      { name: 'Fecha de baja', key: 'cargoEndDateFormatted' },
      { name: 'Cons. Delegado', key: 'isCeoLabel', textAlign: 'center' },
      { name: 'Observaciones', key: 'observations' },
      { name: 'Acciones', key: 'delete' }
    ];
    const data = this.members().map(m => {
      const systemId = m.administrationSystem ?? this.administrationForm.get('administrationSystem')?.value;
      let cargoDisplay = '';
      if (['ADMINISTRADOR_UNICO', 'ADMINISTRADORES_SOLIDARIOS', 'ADMINISTRADORES_MANCOMUNADOS'].includes(systemId || '')) {
        const system = this.adminSystemOptions.find(opt => opt.value === systemId);
        cargoDisplay = system ? system.label : '';
      } else {
        const cargo = this.cargoOptions.find(c => c.value === m.cargo);
        cargoDisplay = cargo ? cargo.label : (m.cargo || '');
      }
      return {
        ...m,
        cargoLabel: cargoDisplay,
        cargoStartDateFormatted: m.cargoStartDate ? new Date(m.cargoStartDate).toLocaleDateString('es-ES') : '-',
        cargoEndDateFormatted: m.cargoEndDate ? new Date(m.cargoEndDate).toLocaleDateString('es-ES') : '-',
        isCeoLabel: m.isCeo ? '✓' : ''
      };
    });
    this.membersTableConfig.set({ headers, data, isPersons: true });
  }

  /** Sistema del miembro que se está editando (el del órgano si es nuevo): decide qué campos de cargo se ven. */
  readonly memberModalSystem = signal<string | null>(null);

  async openMemberModal(index?: number): Promise<void> {
    const sistemaOrgano = this.administrationForm.get('administrationSystem')?.value ?? null;
    this.memberModalSystem.set(index === undefined ? sistemaOrgano : (this.members()[index]?.administrationSystem ?? sistemaOrgano));
    this.memberModalForm.reset({
      id: null,
      associatedClient: null,
      name: '',
      documentNumber: '',
      address: '',
      representativeId: null,
      representativeName: '',
      representativeNif: '',
      cargo: '',
      cargoStartDate: null,
      cargoEndDate: null,
      isCeo: false,
      ceoStartDate: null,
      ceoEndDate: null,
      hasRetribution: false,
      retributionKeys: [],
      retributionAmountA: null,
      retributionAmountE: null,
      retributionAmountG: null,
      inStatutes: false,
      retributionStartDate: null,
      hasContract: false,
      hasMinutes: false,
      socialSecurityRegime: '',
      classification: '',
      selfEmployedChargedTo: '',
      selfEmployedChargedName: '',
      iban: '',
      monthlyNetIncome: null,
      mutualAccidents: '',
      mutualCommonContingencies: '',
      signsCCAA: false,
      certifiesCCAA: false,
      signsIS: false,
      certifiesIS: false,
      signsLaborContracts: false,
      signsLaborDocuments: false,
      observations: ''
    });

    this.representatives.set([]);

    this.memberModalForm.get('name')?.disable();
    this.memberModalForm.get('documentNumber')?.disable();

    const systemId = this.administrationForm.get('administrationSystem')?.value;
    if (
      systemId === 'ADMINISTRADOR_UNICO' ||
      systemId === 'ADMINISTRADORES_SOLIDARIOS' ||
      systemId === 'ADMINISTRADORES_MANCOMUNADOS'
    ) {
      this.memberModalForm.patchValue({
        signsCCAA: true,
        certifiesCCAA: true,
        signsIS: true,
        certifiesIS: true,
        signsLaborContracts: true,
        signsLaborDocuments: true
      });
    }

    if (index === undefined) {
      this.isEditingMember.set(false);
      this.editingMemberId = null;
    } else {
      this.isEditingMember.set(true);
      const member = this.members()[index] as any;
      this.editingMemberId = member.id;
      this.memberModalForm.patchValue(member);
      if (member.representatives) {
        this.representatives.set(member.representatives);
      }
      if (member?.associatedClient) {
        await this.loadRepresentativeOptionsForClient(member.associatedClient as unknown as number);
        const match = this.representativeOptions().find(r =>
          r.name === (member as any).representativeName || r.nif === (member as any).representativeNif
        );
        if (match) {
          this.memberModalForm.patchValue({ representativeId: match.id }, { emitEvent: false });
        }
      }
    }

    this.isMemberModalActive.set(true);
  }

  closeMemberModal(): void {
    this.isMemberModalActive.set(false);
  }

  addRepresentative(): void {
    const repId = this.memberModalForm.get('representativeId')?.value;
    if (!repId) return;

    const rep = this.representativeOptions().find((r) => r.id === repId);
    if (!rep) return;

    const current = this.representatives();
    // Evitar duplicados
    if (current.some((r) => r.id === rep.id)) return;

    this.representatives.update((list) => [...list, rep]);

    // Limpiar selección
    this.memberModalForm.patchValue({
      representativeId: null,
      representativeName: '',
      representativeNif: ''
    });
  }

  removeRepresentative(index: number): void {
    this.representatives.update((list) => list.filter((_, i) => i !== index));
  }

  async saveMember(clientId: number | null, deferPersistence = false): Promise<void> {
    if (this.memberModalForm.invalid) return;
    const formValue = this.memberModalForm.getRawValue();
    // Use UI objects for local state
    const finalData = { ...formValue, representatives: this.representatives() };

    if (this.isEditingMember() && this.editingMemberId !== null) {
      this.members.update(list => list.map(m => m.id === this.editingMemberId ? { ...finalData, id: m.id, administrationSystem: m.administrationSystem } : m));
    } else {
      const newMember = { ...finalData, id: this.nextMemberId(), administrationSystem: this.administrationForm.get('administrationSystem')?.value ?? null } as MemberRow;
      this.members.update(list => [...list, newMember]);
      this.nextMemberId.update(id => id + 1);
    }
    this.buildMembersTableConfig();
    this.closeMemberModal();

    if (deferPersistence) return;

    const adminSystemType = this.administrationForm.get('administrationSystem')?.value;
    let administrationId = this.administrationForm.get('id')?.value;
    if (clientId && adminSystemType && !administrationId) {
      try {
        const administration = await this.clientsService.saveClientAdministrators(this.administrationForm.value, clientId);
        administrationId = administration?.id ?? null;
        this.administrationForm.patchValue({ id: administrationId });
      } catch (error) {
        console.error('Error creating administration', error);
      }
    }
    if (clientId && adminSystemType) {
      const representativesPayload = this.representatives().map(r => ({ id: r.id }));
      this.clientsService.createAdministratorMember({
        ...this.memberModalForm.getRawValue(),
        administrationSystem: this.memberModalSystem() ?? adminSystemType,
        representatives: representativesPayload,
        administration: administrationId ?? undefined
      }).catch(error => {
        console.error('Error creating/updating administrator member', error);
      });
    }
  }

  deleteMember(index: number, deferPersistence = false): void {
    const member = this.members()[index];
    this.members.update(list => list.filter((_, i) => i !== index));
    this.buildMembersTableConfig();
    if (deferPersistence || !member?.id) return;
    this.clientsService.deleteAdministratorMember(member.id).catch(error => {
      console.error('Error deleting administrator member', error);
    });
  }

  private readonly administratorSystemTypes = [
    'ADMINISTRADOR_UNICO',
    'ADMINISTRADORES_SOLIDARIOS',
    'ADMINISTRADORES_MANCOMUNADOS',
  ] as const;

  shouldShowCargoSection(): boolean {
    return !!(this.memberModalSystem() ?? this.administrationForm.get('administrationSystem')?.value);
  }

  shouldShowCargoDropdown(): boolean {
    const systemId = this.memberModalSystem() ?? this.administrationForm.get('administrationSystem')?.value;
    if (!systemId) return false;
    return !this.administratorSystemTypes.includes(systemId as (typeof this.administratorSystemTypes)[number]);
  }

  async loadBankAccountsForMember(clientId: number): Promise<void> {
    try {
      const accounts = await this.clientsService.getClientBankAccounts(clientId);
      this.bankAccounts.set((accounts || []).filter((acc: any) => acc.endDate === null));
    } catch (error) {
      this.bankAccounts.set([]);
    }
  }

  serializeMemberForOperation(
    member: MemberRow & { representatives?: Array<{ id: number; name: string; nif?: string }> },
  ): Record<string, unknown> {
    const classificationMap: Record<string, string> = {
      general: 'GENERAL',
      autonomos: 'SELF_EMPLOYED',
    };
    const chargeMap: Record<string, string> = { empresa: 'COMPANY', persona: 'PERSON' };
    const reps = (member.representatives ?? []).map(r => ({
      id: r.id,
      name: r.name,
      nif: r.nif,
    }));

    const ac = member.associatedClient as { id: number } | number | null | undefined;
    const associatedClientId = typeof ac === 'object' && ac != null ? ac.id : (ac ?? null);

    const payload: Record<string, unknown> = {
      associatedClient: associatedClientId,
      name: member.name,
      documentNumber: member.documentNumber,
      address: member.address,
      representatives: reps.length > 0 ? reps : undefined,
      cargo: member.cargo,
      cargoStartDate: member.cargoStartDate,
      cargoEndDate: member.cargoEndDate,
      administrationSystem: member.administrationSystem ?? this.administrationForm.get('administrationSystem')?.value ?? undefined,
      isCeo: member.isCeo,
      ceoStartDate: member.ceoStartDate,
      ceoEndDate: member.ceoEndDate,
      hasRetribution: member.hasRetribution,
      retributionKeys: member.retributionKeys,
      retributionAmountA: member.retributionAmountA,
      retributionAmountE: member.retributionAmountE,
      retributionAmountG: member.retributionAmountG,
      inStatutes: member.inStatutes,
      retributionStartDate: member.retributionStartDate,
      hasContract: member.hasContract,
      hasMinutes: member.hasMinutes,
      socialSecurityRegime: member.socialSecurityRegime,
      classification: classificationMap[member.classification] ?? member.classification,
      selfEmployedChargedTo: chargeMap[member.selfEmployedChargedTo] ?? member.selfEmployedChargedTo,
      selfEmployedChargedName: member.selfEmployedChargedName,
      iban: member.iban,
      monthlyNetIncome: member.monthlyNetIncome,
      mutualAccidents: member.mutualAccidents,
      mutualCommonContingencies: member.mutualCommonContingencies,
      signsCCAA: member.signsCCAA,
      certifiesCCAA: member.certifiesCCAA,
      signsIS: member.signsIS,
      certifiesIS: member.certifiesIS,
      signsLaborContracts: member.signsLaborContracts,
      signsLaborDocuments: member.signsLaborDocuments,
      observations: member.observations ?? '',
    };
    if (member.id > 0) {
      payload['id'] = member.id;
    }
    return payload;
  }

  serializeMembersForOperation(): Record<string, unknown>[] {
    return this.members().map(m =>
      this.serializeMemberForOperation(m as MemberRow & { representatives?: Array<{ id: number; name: string; nif?: string }> }),
    );
  }

  hydrateFromOperation(admin: { administrationSystem?: string | null; members?: any[] } | null | undefined): void {
    if (!admin) return;

    if (admin.administrationSystem) {
      this.administrationForm.patchValue(
        { administrationSystem: admin.administrationSystem },
        { emitEvent: false },
      );
    }

    const members = admin.members;
    if (!members?.length) return;

    const classificationMapReverse: Record<string, string> = {
      GENERAL: 'general',
      SELF_EMPLOYED: 'autonomos',
    };
    const chargeMapReverse: Record<string, string> = { COMPANY: 'empresa', PERSON: 'persona' };

    const mapped = members.map((m: any, index: number) => {
      const representatives = Array.isArray(m.representatives)
        ? m.representatives.map((r: any, ri: number) => ({
            id: r.id ?? ri + 1,
            name: r.name ?? '',
            nif: r.nif ?? r.documentNumber ?? '',
          }))
        : [];

      return {
        id: m.id ?? index + 1,
        associatedClient: m.associatedClient != null ? { id: m.associatedClient } : null,
        name: m.name ?? '',
        documentNumber: m.documentNumber ?? '',
        address: m.address ?? '',
        representativeName: representatives[0]?.name ?? '',
        representativeNif: representatives[0]?.nif ?? '',
        cargo: m.cargo ?? '',
        cargoStartDate: m.cargoStartDate ? new Date(m.cargoStartDate) : null,
        cargoEndDate: m.cargoEndDate ? new Date(m.cargoEndDate) : null,
        administrationSystem: m.administrationSystem ?? null,
        isCeo: m.isCeo ?? false,
        ceoStartDate: m.ceoStartDate ? new Date(m.ceoStartDate) : null,
        ceoEndDate: m.ceoEndDate ? new Date(m.ceoEndDate) : null,
        hasRetribution: m.hasRetribution ?? false,
        retributionKeys: m.retributionKeys ?? [],
        retributionAmountA: m.retributionAmountA,
        retributionAmountE: m.retributionAmountE,
        retributionAmountG: m.retributionAmountG,
        inStatutes: m.inStatutes ?? false,
        retributionStartDate: m.retributionStartDate ? new Date(m.retributionStartDate) : null,
        hasContract: m.hasContract ?? false,
        hasMinutes: m.hasMinutes ?? false,
        socialSecurityRegime: m.socialSecurityRegime ?? '',
        classification: classificationMapReverse[m.classification] ?? m.classification ?? '',
        selfEmployedChargedTo: chargeMapReverse[m.selfEmployedChargedTo] ?? m.selfEmployedChargedTo ?? '',
        selfEmployedChargedName: m.selfEmployedChargedName ?? '',
        iban: m.iban ?? '',
        monthlyNetIncome: m.monthlyNetIncome,
        mutualAccidents: m.mutualAccidents ?? '',
        mutualCommonContingencies: m.mutualCommonContingencies ?? '',
        signsCCAA: m.signsCCAA ?? false,
        certifiesCCAA: m.certifiesCCAA ?? false,
        signsIS: m.signsIS ?? false,
        certifiesIS: m.certifiesIS ?? false,
        signsLaborContracts: m.signsLaborContracts ?? false,
        signsLaborDocuments: m.signsLaborDocuments ?? false,
        observations: m.observations ?? '',
        representatives,
      } as MemberRow & { representatives: Array<{ id: number; name: string; nif?: string }> };
    });

    this.members.set(mapped);
    const maxId = mapped.reduce((max, m) => Math.max(max, m.id || 0), 0);
    this.nextMemberId.set(maxId + 1);
    this.buildMembersTableConfig();
  }

  private async autofillAddressForMember(clientId: number): Promise<void> {
    try {
      const addresses = await this.clientsService.getClientAddresses(clientId);
      const address = addresses && addresses.length > 0 ? addresses[0] : null;

      if (!address) {
        this.memberModalForm.get('address')?.setValue('');
        return;
      }

      const parts = [address.street, address.number, address.postalCode, address.town, address.province]
        .filter((p: any) => p !== undefined && p !== null && String(p).trim() !== '');

      const fullAddress = parts.length > 0 ? parts.join(', ') : '';
      this.memberModalForm.get('address')?.setValue(fullAddress);
    } catch (error) {
      console.error('[Member Modal] Error loading address:', error);
      this.memberModalForm.get('address')?.setValue('');
    }
  }
}
